import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  isHandoffTraceActive,
  traceFailure,
  traceStep,
  traceUrlPath,
} from '../helpers/handoff-trace.helper';

/** Calls that decide whether a product switch works: sign-in, the destinations list, health. */
const TRACKED_PATHS = [
  'user/verifyfiretoken',
  'datawarehouse/all',
  'services/status',
  'source/file-upload',
  'subscription-levers',
  'warehouse',
];
/** Slower than this is worth a line even when it succeeds. */
const SLOW_MS = 3000;
/**
 * Calls whose failed attempts are steps, not failures: verifyfiretoken is retried by AppService
 * and its final failure is reported by AuthComponent; services/status is polled every 10s by
 * AppComponent while the maintenance card is up, so one report per poll would flood Sentry.
 */
const RETRIED_ELSEWHERE_PATHS = ['user/verifyfiretoken', 'services/status'];

/** Path with numeric segments as `:id`, so a Sentry message groups by endpoint, not by user. */
export function toEndpointPath(path: string): string {
  return path.replace(/\/\d+(?=\/|$)/g, '/:id');
}

/**
 * Logs timing and status of the tracked calls into the handoff timeline. A status of 0 means no
 * response the browser could read: a network drop, CORS, or the load balancer answering 404
 * while no backend pod is ready.
 */
@Injectable()
export class HandoffTraceInterceptor implements HttpInterceptor {
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const path = traceUrlPath(request.url);
    if (!isHandoffTraceActive() || !TRACKED_PATHS.some((tracked) => path.includes(tracked))) {
      return next.handle(request);
    }
    const startedAt = Date.now();
    const base = { method: request.method, path };
    traceStep('http_start', base);
    return next.handle(request).pipe(
      tap({
        next: (event) => {
          if (event instanceof HttpResponse) {
            const ms = Date.now() - startedAt;
            traceStep(ms >= SLOW_MS ? 'http_end_SLOW' : 'http_end', { ...base, status: event.status, ms });
          }
        },
        error: (error: HttpErrorResponse) => {
          const ms = Date.now() - startedAt;
          const data = { ...base, status: error.status, ms, statusText: error.statusText };
          const retriedElsewhere = RETRIED_ELSEWHERE_PATHS.some((retried) => path.includes(retried));
          if ((error.status === 0 || error.status >= 500) && !retriedElsewhere) {
            traceFailure(`${toEndpointPath(path)} -> ${error.status}`, data);
          } else {
            traceStep('http_error', data);
          }
        },
      })
    );
  }
}
